import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import api from '@app/services/api';
import SearchableSelect from '@platform/ui/SearchableSelect';
import MultiSelectEditor from '@modules/database/work-items/frontend/editors/MultiSelectEditor';
import RichTextEditor from '@modules/database/work-items/frontend/editors/RichTextEditor';
import DateField from '@modules/database/work-items/frontend/editors/DateField';
import { adfToTipTap, tipTapToAdf } from '@modules/database/work-items/frontend/utils/adfConverter';
import resolveImageRefs from '@modules/database/work-items/frontend/utils/resolveImageRefs';
import '@modules/database/work-items/frontend/components/TransitionScreenModal/TransitionScreenModal.css';

// System fields that need a user (accountId) picker.
const USER_FIELDS = new Set(['assignee', 'reporter']);

// Decide which control to render for a screen field from its editmeta schema
// + whether it carries allowed option values.
function controlType(fieldId, meta) {
  const t = meta && meta.schema && meta.schema.type;
  const hasOptions = ((meta && meta.allowedValues) || []).length > 0;
  if (fieldId === 'resolution') return 'resolution';
  if (USER_FIELDS.has(fieldId) || t === 'user') return 'user';
  // Rich-text (ADF) fields: the description system field, or a "textarea"
  // custom field. Same detection the main issue screen uses.
  const custom = meta && meta.schema && meta.schema.custom;
  if (fieldId === 'description' || (typeof custom === 'string' && custom.includes('textarea'))) return 'rich-text';
  if (t === 'array') return hasOptions ? 'multiselect' : 'text';
  if (t === 'option' || hasOptions) return 'select';
  if (t === 'date') return 'date';
  if (t === 'datetime') return 'datetime';
  if (t === 'number') return 'number';
  return 'text';
}

// An ADF doc with no text and no meaningful leaf node is visually empty — the
// empty-paragraph shape the editor emits when cleared. Without this, isEmpty
// would treat it as a filled object, bypassing required checks and preventing a
// prefilled rich-text field from being cleared to null on submit.
function isEmptyAdf(doc) {
  const LEAF = new Set(['media', 'mediaSingle', 'mediaGroup', 'mention', 'emoji', 'inlineCard', 'blockCard', 'rule', 'table']);
  // Guard non-array node lists and null elements so a malformed doc is treated
  // as empty rather than throwing during render.
  const has = nodes => Array.isArray(nodes) && nodes.some(n =>
    n && (n.type === 'text' ? String(n.text || '').trim() !== ''
      : LEAF.has(n.type) ? true
        : has(n.content)));
  return !has(doc && doc.content);
}

function isEmpty(v) {
  if (v == null || v === '') return true;
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === 'object' && v.type === 'doc') return isEmptyAdf(v);
  return false;
}

// A modal that renders a workflow transition's screen: the user fills in the
// screen's fields (required enforced) + an optional comment, then the parent
// fires the transition with { fields, comment }. Only opened for transitions
// whose `hasScreen` is true. Reuses the app's field styling + SearchableSelect
// / MultiSelectEditor so it matches the rest of the PWA in light + dark mode.
//
// bulkCount (optional): the BulkChangeModal opens this ONCE for a whole
// group of work items, using the first key to fetch the screen. In that
// mode nothing is prefilled (the values would be one item's, not the
// group's), the header says how many items the values apply to, and
// onSubmit only collects { fields, comment } — the bulk run fires them.
function TransitionScreenModal({ issueKey, transition, onClose, onSubmit, bulkCount = 0 }) {
  const bulk = bulkCount > 0;
  // Cached user lookup for the transition screen's user pickers.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fields, setFields] = useState({});      // fieldId -> editmeta meta
  const [order, setOrder] = useState([]);        // fieldId[] (screen order)
  const [values, setValues] = useState({});      // fieldId -> value
  const [resolutionOptions, setResolutionOptions] = useState([]); // [{id, value}]
  const [userOptions, setUserOptions] = useState([]);             // [{id, value}]
  const [scopedUserOptions, setScopedUserOptions] = useState({}); // fieldId → options for context-restricted user fields
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  // Issue attachment list — fetched only when the screen has a rich-text
  // field, so embedded media (jira-media:// refs) resolve to real file chips
  // with filenames instead of dead "Jira attachment" placeholders.
  const [attachments, setAttachments] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await api.getTransitionScreen(issueKey, transition.id);
        if (cancelled) return;
        const f = (data && data.fields) || {};
        // Line items tables are edited on the work item, never on a transition screen.
        const ord = Object.keys(f).filter(fid => fid !== 'issuelinks' && f[fid]?.schema?.type !== 'lineitems' && f[fid]?.schema?.type !== 'whiteboard');
        setFields(f);
        setOrder(ord);
        // Prefill each control with the field's current value (Jira parity).
        // The server sends null for values with no faithful control (non-ADF
        // structured values) — skip those and empty arrays so they stay blank.
        const initVals = {};
        for (const fid of ord) {
          if (bulk) { delete f[fid].currentValue; continue; } // group-wide: nothing to prefill
          const cv = f[fid] ? f[fid].currentValue : undefined;
          if (cv == null) continue;
          if (Array.isArray(cv) && cv.length === 0) continue;
          initVals[fid] = cv;
        }
        setValues(initVals);
        const needsResolution = !!f.resolution;
        const needsUsers = ord.some(fid => USER_FIELDS.has(fid) || (f[fid].schema && f[fid].schema.type === 'user'));
        // Rich-text fields may embed media nodes; fetch the attachment list
        // BEFORE loading finishes so resolveImageRefs can name/link them when
        // the editor first mounts (RichTextEditor reads value on mount only).
        const needsAttachments = ord.some(fid => controlType(fid, f[fid]) === 'rich-text');
        const [rs, us, attIssue] = await Promise.all([
          needsResolution ? api.getResolutions().catch(() => []) : Promise.resolve(null),
          needsUsers ? api.getUsers('').catch(() => []) : Promise.resolve(null),
          needsAttachments ? api.getIssue(issueKey, 'attachment').catch(() => null) : Promise.resolve(null),
        ]);
        if (cancelled) return;
        if (rs) setResolutionOptions((rs || []).map(r => ({ id: String(r.id || r.value), value: r.name || r.value })));
        const toOpts = (list) => (list || []).map(u => ({ id: String(u.accountId || u.id), value: u.displayName || u.name || u.accountId }));
        if (us) setUserOptions(toOpts(us));
        // A custom user field lists only its context's allowed users (Jira's
        // group / project-role restriction); the server derives project and
        // work type from the item. Bulk (no single item) keeps the full list.
        const scopedIds = issueKey ? ord.filter(fid => fid.startsWith('customfield_') && f[fid].schema && f[fid].schema.type === 'user') : [];
        if (scopedIds.length) {
          const pairs = await Promise.all(scopedIds.map(fid => api.getUsers('', { fieldId: fid, issueKey })
            .then(list => [fid, toOpts(list)]).catch(() => [fid, null])));
          if (cancelled) return;
          setScopedUserOptions(Object.fromEntries(pairs.filter(([, l]) => Array.isArray(l))));
        }
        if (attIssue && Array.isArray(attIssue.fields && attIssue.fields.attachment)) {
          setAttachments(attIssue.fields.attachment);
        }
      } catch (e) {
        if (!cancelled) setError('Could not load the transition screen.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [issueKey, transition.id, bulk]);

  const ctMap = useMemo(() => {
    const m = {};
    for (const fid of order) m[fid] = controlType(fid, fields[fid]);
    return m;
  }, [order, fields]);

  const setVal = useCallback((fid, v) => {
    setValues(prev => ({ ...prev, [fid]: v }));
  }, []);

  const missingRequired = order.filter(fid => fields[fid] && fields[fid].required && isEmpty(values[fid]));

  const submit = useCallback(async () => {
    if (missingRequired.length || submitting) return;
    const payload = {};
    for (const fid of order) {
      const v = values[fid];
      const empty = isEmpty(v);
      // Was this field prefilled with a value we could show? (ADF/structured
      // fields come back with currentValue = null and are left blank.)
      const cv = fields[fid] ? fields[fid].currentValue : null;
      const wasPrefilled = cv != null && !(Array.isArray(cv) && cv.length === 0);
      // Jira parity: a transition screen is authoritative for its fields, so
      // clearing a prefilled field and submitting CLEARS it. But never clear a
      // field we couldn't prefill (its blank is our limitation, not the user's
      // intent) — leave those untouched.
      if (empty && !wasPrefilled) continue;
      if (empty) { payload[fid] = null; continue; }
      // User fields go to the backend as { accountId }; everything else as-is
      // (option id / array of ids / text — decodeSetFieldValue handles each).
      payload[fid] = ctMap[fid] === 'user' ? { accountId: v } : v;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await onSubmit(payload, comment.trim() ? comment.trim() : null);
      // Parent unmounts us on success.
    } catch (res) {
      const msg = (res && (Array.isArray(res.reasons) && res.reasons.length
        ? res.reasons.join(' ')
        : res.error)) || 'Transition failed.';
      setSubmitError(msg);
      setSubmitting(false);
    }
  }, [missingRequired.length, submitting, order, values, ctMap, fields, comment, onSubmit]);

  function renderControl(fid, meta) {
    const ct = ctMap[fid];
    const v = values[fid];
    if (ct === 'resolution' || ct === 'select' || ct === 'user') {
      const opts = ct === 'resolution' ? resolutionOptions
        : ct === 'user' ? (scopedUserOptions[fid] || userOptions)
        : (meta.allowedValues || []);
      return (
        <SearchableSelect
          options={opts}
          value={v || ''}
          onChange={val => setVal(fid, val || null)}
          disabled={submitting}
          placeholder={ct === 'user' ? 'Search users…' : 'Search…'}
          noneLabel="— None —"
        />
      );
    }
    if (ct === 'rich-text') {
      // values[fid] holds ADF (prefilled from the server, or converted back on
      // edit). Seed the editor with TipTap; on change, store ADF again so submit
      // stays generic and an untouched prefill round-trips the exact document.
      // Resolve embedded media refs against the attachment list (same as the
      // issue screen's edit-mode seeding) so files render as named, clickable
      // chips. Round-trip safe: resolved images keep data-media-id, which
      // tipTapToAdf converts back to the original media[type=file] node.
      const seeded = adfToTipTap(v);
      resolveImageRefs(seeded, attachments);
      return (
        <div className="transition-screen-richtext">
          <RichTextEditor
            value={seeded}
            onChange={json => setVal(fid, tipTapToAdf(json))}
            disabled={submitting}
            onFileUpload={async (file) => {
              const result = await api.uploadAttachment(issueKey, file);
              const a = result[0];
              return { ...a, content: api.getAttachmentContentUrl(a.id), mediaApiFileId: a.mediaApiFileId || null };
            }}
          />
        </div>
      );
    }
    if (ct === 'multiselect') {
      return (
        <MultiSelectEditor
          value={Array.isArray(v) ? v : []}
          onChange={arr => setVal(fid, arr)}
          disabled={submitting}
          options={meta.allowedValues || []}
          collapsible
        />
      );
    }
    if (ct === 'date') {
      return <DateField className="create-issue-input" value={v || ''} onChange={(d) => setVal(fid, d || null)} disabled={submitting} />;
    }
    if (ct === 'datetime') {
      return <input className="create-issue-input" type="datetime-local" value={v || ''} onChange={e => setVal(fid, e.target.value || null)} disabled={submitting} />;
    }
    if (ct === 'number') {
      return <input className="create-issue-input" type="number" value={v == null ? '' : v} onChange={e => setVal(fid, e.target.value)} disabled={submitting} />;
    }
    return <textarea className="create-issue-input" rows={3} value={v || ''} onChange={e => setVal(fid, e.target.value)} disabled={submitting} />;
  }

  return createPortal(
    <div className={`modal-overlay${bulk ? ' tsm-over-bulk' : ''}`} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-content transition-screen-modal">
        <div className="modal-header">
          <h3>
            {transition.name || (transition.to && transition.to.name) || 'Transition'}
            {bulk && <span className="tsm-bulk-count"> · applies to {bulkCount} work item{bulkCount !== 1 ? 's' : ''}</span>}
          </h3>
          <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="modal-body">
          {loading && <div className="tsm-status">Loading screen…</div>}
          {error && <div className="tsm-status tsm-error">{error}</div>}
          {!loading && !error && order.map(fid => {
            const meta = fields[fid];
            return (
              <div className="create-issue-field" key={fid}>
                <label className="create-issue-label">
                  {meta.name || fid}
                  {meta.required && <span className="tsm-req"> *</span>}
                </label>
                {renderControl(fid, meta)}
              </div>
            );
          })}
          {!loading && !error && (
            <div className="create-issue-field">
              <label className="create-issue-label">Comment</label>
              <textarea
                className="create-issue-input"
                rows={3}
                value={comment}
                onChange={e => setComment(e.target.value)}
                placeholder="Add a comment (optional)…"
                disabled={submitting}
              />
            </div>
          )}
          {submitError && <div className="tsm-status tsm-error">{submitError}</div>}
        </div>
        <div className="modal-footer">
          <button className="tsm-btn tsm-btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
          <button
            className="tsm-btn tsm-btn-primary"
            onClick={submit}
            disabled={submitting || loading || missingRequired.length > 0}
            title={missingRequired.length ? `Required: ${missingRequired.map(f => (fields[f].name || f)).join(', ')}` : ''}
          >
            {submitting ? 'Applying…' : bulk ? 'Use these values' : (transition.name || 'Apply')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default TransitionScreenModal;
